import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Divider, Paper, Stack, Typography,
  alpha, useTheme,
} from '@mui/material';
import menuService from '../../services/menuService';
import type { MenuRenderResponse } from '../../types/menu';

interface Props {
  venueId: string;
  menuId: string;
}

// Phase 4.L — operator-facing language. Avoids "storage-only" / "depletion"
// terms; honest about the link being inactive without engineering jargon.
const INERT_FOOTER =
  'Recipes on this menu are not active for sales or stock yet.';

/**
 * Admin preview for a menu. Loads /menu/:menuId/render once on mount and
 * shows the structured DTO as sections of items with per-format pricing.
 *
 * Phase 4.B.c — also surfaces minimal WINE-specific detail (vintage chip and
 * scores) when `item.kind === 'WINE'` and `item.wineDetails` is present.
 * binNumber is never displayed here either — admin-only and only relevant
 * to staff-facing surfaces (not preview).
 *
 * Does NOT reuse WineListMenuView. The Menu engine has its own simple layout.
 */
const MenuPreviewPanel: React.FC<Props> = ({ venueId, menuId }) => {
  const theme = useTheme();
  const [data, setData] = useState<MenuRenderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    menuService.renderMenu(venueId, menuId)
      .then(setData)
      .catch((err: any) => {
        if (err?.response?.data?.code === 'MENU_ARCHIVED') {
          setError('Archived menus cannot be previewed.');
        } else {
          setError(err?.response?.data?.message ?? 'Could not load preview. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [venueId, menuId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 1 }}>{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  const { menu, sections, _meta } = data;
  const currencyCode = menu.defaultCurrency ?? '';

  const formatPrice = (price: number | null, perItemCurrency: string | null): string => {
    if (price == null) return '—';
    const code = perItemCurrency ?? currencyCode;
    return code ? `${code} ${price.toFixed(2)}` : price.toFixed(2);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Skipped items advisory — fires when an item exists on the menu whose
          kind isn't yet rendered by the engine (Phase 4.B renders COCKTAIL +
          WINE; everything else falls into the skipped count). */}
      {_meta.skippedItemCount > 0 && (
        <Alert severity="info" sx={{ borderRadius: 1, mb: 2 }}>
          {_meta.skippedItemCount} item{_meta.skippedItemCount === 1 ? ' was' : 's were'}{' '}
          excluded from this preview because their kind isn't supported yet.
        </Alert>
      )}

      {/* Menu header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{menu.name}</Typography>
        {menu.venueName && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {menu.venueName}
          </Typography>
        )}
        {menu.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {menu.description}
          </Typography>
        )}
      </Box>

      {sections.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This menu has no live items yet. Add items from the Items tab.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {sections.map(section => (
            <Box key={section.id ?? 'other'}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {section.name}
                </Typography>
                {section.visibility === 'HIDDEN' && (
                  <Chip label="HIDDEN" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                )}
              </Box>
              {section.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {section.description}
                </Typography>
              )}
              <Divider sx={{ mb: 1.5 }} />
              {section.items.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No items in this section.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {section.items.map(item => (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        opacity: item.visibility === 'HIDDEN' ? 0.6 : 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                            {item.kind === 'WINE' && (
                              <Chip
                                label="WINE"
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 18, fontFamily: 'monospace' }}
                              />
                            )}
                            <Typography variant="subtitle2" fontWeight={700}>
                              {item.name ?? '(unnamed)'}
                            </Typography>
                            {item.kind === 'WINE' && item.wineDetails?.wineDisplay?.displayVintage && (
                              <Typography
                                variant="caption"
                                sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 600 }}
                              >
                                {item.wineDetails.wineDisplay.displayVintage}
                              </Typography>
                            )}
                            {item.visibility === 'HIDDEN' && (
                              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 600 }}>
                                HIDDEN
                              </Typography>
                            )}
                          </Box>
                          {item.subtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {item.subtitle}
                            </Typography>
                          )}
                          {item.kind === 'WINE' && (item.wineDetails?.scores?.length ?? 0) > 0 && (
                            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                              {(item.wineDetails?.scores ?? []).map(s => (
                                <Chip
                                  key={s.source}
                                  label={`${s.source} ${s.score}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 18, fontFamily: 'monospace' }}
                                />
                              ))}
                            </Stack>
                          )}
                          {item.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontStyle: 'italic' }}>
                              {item.notes}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {formatPrice(item.basePrice, item.currency)}
                          </Typography>
                        </Box>
                      </Box>

                      {item.formats.length > 0 && (
                        <Box sx={{ mt: 1.5, pl: 0, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.6)}`, pt: 1.25 }}>
                          <Stack spacing={0.75}>
                            {item.formats.map(f => (
                              <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 110 }}>
                                  {f.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {f.formatKind === 'VOLUME' && f.volumeMl != null ? `${f.volumeMl} ml` : f.formatKind}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                {f.consumptionRecipeId && (
                                  <Chip
                                    label="Recipe linked"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 18 }}
                                  />
                                )}
                                <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                  {formatPrice(f.price, item.currency)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
        <Typography variant="caption" color="text.secondary">
          {INERT_FOOTER}
        </Typography>
      </Box>
    </Box>
  );
};

export default MenuPreviewPanel;
